import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * "Coach mode" — the user is acting on behalf of a managed ghost athlete.
 *
 * When set, the PageHeader's CoachContextStrip is rendered and any workout
 * actions triggered from the athlete's profile insert rows under that ghost's
 * user_id (RLS allows it via `managed_by = current_profile_id()`).
 *
 * Persistence is `sessionStorage`, not `localStorage`: the context is a
 * within-session UX hint, not a long-lived preference. Closing the tab clears
 * it so a coach who leaves mid-session doesn't return tomorrow still "in"
 * someone else's workout.
 */
export interface CoachContextState {
  readonly active: { readonly athleteId: string; readonly athleteName: string } | null;
  readonly enter: (athleteId: string, athleteName: string) => void;
  readonly exit: () => void;
  /** Clear context only if it currently matches the given athlete id. */
  readonly clearIfMatches: (athleteId: string) => void;
}

export const useCoachContext = create<CoachContextState>()(
  persist(
    (set, get) => ({
      active: null,
      enter: (athleteId, athleteName) => {
        set({ active: { athleteId, athleteName } });
      },
      exit: () => {
        set({ active: null });
      },
      clearIfMatches: (athleteId) => {
        if (get().active?.athleteId === athleteId) set({ active: null });
      },
    }),
    {
      name: 'trenora.coach-context',
      // sessionStorage so the context dies with the tab — see file-level
      // comment for the reasoning.
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
